import React from 'react';
import { timelineData, ctaLink } from '../data/mock';
import { Rocket, TrendingUp, Users } from 'lucide-react';

const iconMap = {
  0: Rocket,
  1: TrendingUp,
  2: Users
};

const TimelineSection = () => {
  return (
    <section className="relative py-24 lg:py-32 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-slate-950 via-slate-900/30 to-slate-950" />
      <div className="absolute top-0 left-0 w-[800px] h-[800px] bg-cyan-500/5 rounded-full blur-[150px]" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="inline-block px-4 py-2 bg-cyan-500/10 border border-cyan-500/20 rounded-full text-sm font-medium text-cyan-400 mb-6">
            Your Journey
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
            What to Expect in{' '}
            <span className="bg-gradient-to-r from-cyan-400 to-teal-400 bg-clip-text text-transparent">
              60 Days
            </span>
          </h2>
          <p className="text-lg text-slate-400">
            From setup to closing clients—here's your realistic timeline with outwrite.ai
          </p>
        </div>

        {/* Timeline */}
        <div className="relative">
          {/* Timeline Line */}
          <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-1 bg-gradient-to-r from-cyan-500/20 via-teal-500/40 to-emerald-500/20 rounded-full" />

          <div className="grid lg:grid-cols-3 gap-8">
            {timelineData.map((item, index) => {
              const Icon = iconMap[index];
              return (
                <div key={index} className="relative">
                  {/* Timeline Dot */}
                  <div className="hidden lg:flex absolute -top-3 left-1/2 -translate-x-1/2 w-6 h-6 rounded-full bg-gradient-to-r from-cyan-500 to-teal-500 border-4 border-slate-950 items-center justify-center z-10">
                    <div className="w-2 h-2 rounded-full bg-white" />
                  </div>

                  {/* Card */}
                  <div className="group relative p-8 rounded-2xl bg-gradient-to-b from-white/[0.05] to-transparent border border-white/10 hover:border-cyan-500/30 transition-all duration-500 lg:mt-8">
                    {/* Day Badge */}
                    <div className="absolute -top-4 left-6 px-4 py-1.5 bg-gradient-to-r from-cyan-500 to-teal-500 rounded-full text-sm font-bold text-slate-950">
                      {item.day}
                    </div>

                    {/* Icon */}
                    <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-cyan-500/20 to-teal-500/20 border border-cyan-500/20 flex items-center justify-center mb-6 mt-4 group-hover:scale-110 transition-transform duration-300">
                      <Icon className="w-7 h-7 text-cyan-400" />
                    </div>

                    {/* Content */}
                    <h3 className="text-2xl font-bold text-white mb-2">
                      {item.title}
                    </h3>
                    <p className="text-slate-400 mb-4">
                      {item.description}
                    </p>

                    {/* Metric */}
                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-cyan-500/10 to-teal-500/10 border border-cyan-500/20 rounded-full">
                      <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                      <span className="text-sm font-semibold text-emerald-300">{item.metric}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* CTA */}
        <div className="text-center mt-16">
          <a
            href={ctaLink}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-cyan-400 hover:text-cyan-300 font-medium text-lg transition-colors duration-300 group"
          >
            Start your journey today
            <span className="group-hover:translate-x-1 transition-transform duration-300">→</span>
          </a>
        </div>
      </div>
    </section>
  );
};

export default TimelineSection;
